import { useFormContext } from "react-hook-form";
import { useParams } from "@tanstack/react-router";
import useRequest from "@/lib/request/useRequest";
import { FeatureCollection, MultiPolygon } from "geojson";
import { Plot, TrendlineTable } from "@/types/plot";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    getKeyValue,
    Spinner,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    SortDescriptor,
} from "@heroui/react";
import { UseQueryResult } from "@tanstack/react-query";
import dayjs from "dayjs";
import { roundTo } from "@/lib/utils/numbers";
import { calculateYBands } from "@/lib/utils/sensitivity";
import useMap from "@/lib/map/useMap";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "@/lib/utils/map";
import {
    FillLayerSpecification,
    LineLayerSpecification,
    SymbolLayerSpecification,
} from "mapbox-gl";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { useTranslation } from "react-i18next";

const TABLE_SOURCE = "trendline-table-source";
const FILL_LAYER: FillLayerSpecification = {
    source: TABLE_SOURCE,
    id: "trendline-table-fill",
    type: "fill",
    filter: ["==", ["geometry-type"], "Polygon"],
    layout: {},
    paint: {
        "fill-color": "#FFF",
        "fill-opacity": 0.5,
    },
};
const LINE_LAYER: LineLayerSpecification = {
    source: TABLE_SOURCE,
    id: "trendline-table-line",
    type: "line",
    layout: {},
    paint: {
        "line-color": "#FFF",
        "line-width": 3,
    },
    minzoom: 14,
};
const SYMBOL_LAYER: SymbolLayerSpecification = {
    source: TABLE_SOURCE,
    id: "trendline-table-symbol",
    type: "symbol",
    layout: {
        "text-field": ["get", "pair_id"],
        "text-size": 12,
    },
};

function SITable({
    trendlineQuery,
}: {
    trendlineQuery: UseQueryResult<TrendlineTable[]>;
}) {
    const { t } = useTranslation("insights");
    const { map, mapLoaded } = useMap();
    const { useGetRequest } = useRequest();
    const { fieldId } = useParams({
        from: "/dashboard/fields/$fieldId/insights/",
    });

    const f = useFormContext();
    const [startDate, endDate, pairIds] = f.watch([
        "startDate",
        "endDate",
        "pairIds",
    ]);

    const uploadedPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/uploaded_plots/`,
        params: { boundary: "analytic" },
    });
    const appliedPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/applied_plots/`,
        params: { boundary: "analytic" },
    });

    const plots = useMemo(() => {
        return [
            ...(uploadedPlots?.data?.features ?? []),
            ...(appliedPlots?.data?.features ?? []),
        ];
    }, [appliedPlots?.data?.features, uploadedPlots?.data?.features]);

    const dataInTimeRange = useMemo(() => {
        return (
            trendlineQuery?.data?.filter(
                (row) =>
                    dayjs(row.capture_date).isAfter(dayjs(startDate)) &&
                    dayjs(row.capture_date).isBefore(dayjs(endDate)) &&
                    (!pairIds?.length ||
                        pairIds?.includes(row.pair_id.toString())),
            ) ?? []
        ).map((row, index) => {
            return {
                ...row,
                index,
                sensitized_si: roundTo(row.sensitized_si * 100, 1),
                area_rep: roundTo(row.area_rep),
                percent_area_rep: roundTo(row.percent_area_rep),
                ndre_mean: roundTo(row.ndre_mean, 4),
            };
        });
    }, [endDate, pairIds, startDate, trendlineQuery?.data]);

    const { fieldQuery } = useFieldDashboard() ?? {};

    const bands = useMemo(
        () =>
            calculateYBands(
                fieldQuery?.data?.insight_sensitivity ?? "moderate",
            ),
        [fieldQuery?.data?.insight_sensitivity],
    );

    useEffect(() => {
        if (!map || !mapLoaded) return;

        addUpdateGeojsonSource(map, TABLE_SOURCE, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
        });

        addUpdateGeojsonLayers(map, [FILL_LAYER, LINE_LAYER, SYMBOL_LAYER]);

        return () => {
            if (map && mapLoaded) {
                removeLayersIfExists(map, [
                    FILL_LAYER.id,
                    LINE_LAYER.id,
                    SYMBOL_LAYER.id,
                ]);
                removeSourcesIfExists(map, [TABLE_SOURCE]);
            }
        };
    }, [map, mapLoaded]);

    const onMouseEnter = useCallback(
        (plotId: number) => {
            if (!map || !mapLoaded) return;

            const plot = plots?.find((plot) => plot.id == plotId);

            if (!plot) return;

            addUpdateGeojsonSource(map, TABLE_SOURCE, {
                type: "geojson",
                data: plot,
                tolerance: 0,
            });
        },
        [map, mapLoaded, plots],
    );
    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
        column: "capture_date",
        direction: "ascending",
    });
    const sortedItems = useMemo(() => {
        const { column, direction } = sortDescriptor;
        if (!column) return dataInTimeRange;
        const sorted = sortArrayByKey(
            dataInTimeRange,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            column as any,
            direction === "descending" ? "desc" : "asc",
        );
        return sorted;
    }, [dataInTimeRange, sortDescriptor]);

    const COLUMNS = useMemo(() => [
        { key: "capture_date", label: t("tableColumns.date") },
        { key: "plot_type", label: t("tableColumns.plotType") },
        { key: "pair_id", label: t("tableColumns.pairId") },
        { key: "sensitized_si", label: t("tableColumns.siPercent") },
        { key: "si_status", label: t("tableColumns.status") },
        { key: "ndre_mean", label: t("tableColumns.ndre") },
        { key: "area_rep", label: t("tableColumns.rangeAc") },
        { key: "percent_area_rep", label: t("tableColumns.weightPercent") },
    ], [t]);

    if (!appliedPlots.isFetched || !uploadedPlots.isFetched) {
        return <Skeleton className="h-[500px] w-full rounded-md p-2" />;
    }

    return (
        <div className="h-full overflow-hidden select-none">
            <Table
                isHeaderSticky
                isVirtualized
                maxTableHeight={500}
                aria-label={t("ariaLabels.trendline")}
                onSortChange={setSortDescriptor}
                sortDescriptor={sortDescriptor}
                classNames={{
                    th: "px-[0.25rem]",
                    td: "px-[0.25rem]",
                    wrapper: "p-0 shadow-none rounded-none",
                }}
            >
                <TableHeader columns={COLUMNS}>
                    {(column) => (
                        <TableColumn key={column.key} allowsSorting>
                            {column.label}
                        </TableColumn>
                    )}
                </TableHeader>
                <TableBody
                    items={sortedItems}
                    emptyContent={t("messages.noDataDateRange")}
                    isLoading={trendlineQuery.isFetching}
                    loadingContent={<Spinner label={t("messages.loading")} />}
                >
                    {(item) => (
                        <TableRow
                            key={item.index}
                            onMouseEnter={() => {
                                onMouseEnter(item.plot_id);
                            }}
                            className="hover:bg-gray-100 rounded-lg"
                        >
                            {(columnKey) => {
                                if (columnKey === "sensitized_si") {
                                    const si = getKeyValue(item, columnKey);
                                    const color =
                                        bands.find(
                                            (band) =>
                                                si > band.y0 && si <= band.y1,
                                        )?.color ?? "white";
                                    return (
                                        <TableCell>
                                            <div
                                                style={{
                                                    backgroundColor: color,
                                                }}
                                                className="rounded-md font-medium  p-1 m-0 text-center justify-self-start"
                                            >
                                                {si}%
                                            </div>
                                        </TableCell>
                                    );
                                }
                                return (
                                    <TableCell className="text-xs">
                                        {getKeyValue(item, columnKey)}
                                    </TableCell>
                                );
                            }}
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

export default SITable;
